// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Helpers to resolve the Public API URL injected by cfn_bucket_loader at
 * deploy time (`window.public_api`). The Public API is reachable directly
 * (CloudFront + x-api-key) without authentication, so this never goes
 * through the BFF.
 */

/**
 * @returns {string} Public API base URL with trailing slashes stripped.
 */
export function getPublicApiUrl() {
  const fromWindow = typeof window !== 'undefined' ? window.public_api : '';
  const url = fromWindow || process.env.VUE_APP_PUBLIC_API_URL || '';
  return url.replace(/\/+$/, '');
}

/**
 * @returns {string} Wildcard domain that hosts the tenant-admin SPA.
 *   Tenant admin is reached via `https://{tenant_code}.{base_domain}/`.
 *   Empty string when not deployed (e.g., local dev without injected
 *   api_endpoints.js).
 */
export function getTenantAdminBaseDomain() {
  const fromWindow =
    typeof window !== 'undefined' ? window.TENANT_ADMIN_BASE_DOMAIN : '';
  return fromWindow || process.env.VUE_APP_TENANT_ADMIN_BASE_DOMAIN || '';
}

/**
 * Build the tenant-admin SPA URL for a tenant.
 * @param {string} tenantCode the tenant_code value (left-most subdomain label).
 * @returns {string} Fully-qualified URL, or '' when the base domain is unset.
 */
export function buildTenantAdminUrl(tenantCode) {
  const baseDomain = getTenantAdminBaseDomain();
  if (!baseDomain || !tenantCode) return '';
  return `https://${tenantCode}.${baseDomain}/`;
}
